import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notaEntradaService, NotaEntrada, NotaEntradaProduto, NotaEntradaProdutoCommand, NotaEntradaRequest } from '../../services/notaEntrada';
import { fornecedorService } from '../../services/fornecedor';
import { produtoService } from '../../services/produto';
import DatePickerField from '../common/DatePickerField';

interface NotaEntradaFormProps {
  initialData?: NotaEntrada;
}

const NotaEntradaForm: React.FC<NotaEntradaFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(initialData);

  // Estado do formulário
  const [formData, setFormData] = useState<{
    idFornecedor: string | number;
    dataCompra: string;
    dataEntradaEmEstoque: string;
  }>({
    idFornecedor: initialData?.idFornecedor || '',
    dataCompra: initialData?.dataCompraString || '',
    dataEntradaEmEstoque: initialData?.dataEntradaEmEstoqueString || '',
  });

  // Estado dos produtos
  const [produtos, setProdutos] = useState<NotaEntradaProdutoCommand[]>(
    initialData?.produtos?.map(produto => ({
      ...produto,
      precoCompra: produto.precoUnitario.toString() // Mapear precoUnitario para precoCompra como string
    })) || []
  );

  // Estado para valores formatados nos inputs
  const [produtoValues, setProdutoValues] = useState<Record<string, string>>({});

  // Estado de erros
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [produtosDialogOpen, setProdutosDialogOpen] = useState(false);
  const [produtosParaAdicionar, setProdutosParaAdicionar] = useState<any[]>([]);

  const filterProdutos = createFilterOptions({
    stringify: (option: any) => `${option.referencia || ''} ${option.nomeProduto || option.nome || ''}`.toLowerCase(),
    limit: 50,
  });

  // Buscar fornecedores
  const { data: fornecedores = [] } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: fornecedorService.getAll,
  });

  // Buscar produtos
  const { data: produtosDisponiveis = [] } = useQuery({
    queryKey: ['produtos'],
    queryFn: produtoService.getAll,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: NotaEntradaRequest) => 
      isEditing ? notaEntradaService.update(initialData!.id, data) : notaEntradaService.create(data),
    onSuccess: () => {
      // Invalidar cache para forçar recarregamento dos dados
      queryClient.invalidateQueries({ queryKey: ['notasEntrada'] });
      navigate('/entradas');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar nota de entrada:', error);
    }
  });

  // Funções para manipular produtos
  const openProdutosDialog = () => {
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(true);
  };

  const closeProdutosDialog = () => {
    setProdutosDialogOpen(false);
    setProdutosParaAdicionar([]);
  };

  const handleAdicionarProdutos = () => {
    const novosProdutos: NotaEntradaProdutoCommand[] = produtosParaAdicionar.map((p: any) => ({
      id: 0,
      idNotaEntrada: 0,
      idProduto: Number(p.id) || 0,
      nomeProduto: p.nomeProduto || '',
      referencia: p.referencia || '',
      quantidade: '',
      precoCompra: '0.00',
      valorTotal: 0,
      CodigoDeBarras: [],
      IdSacola: [],
    }));
    setProdutos((prev) => [...prev, ...novosProdutos]);
    setProdutosParaAdicionar([]);
    setProdutosDialogOpen(false);
  };

  const removeProduto = (index: number) => {
    setProdutos(produtos.filter((_, i) => i !== index));
  };

  const updateProduto = (index: number, field: string, value: any) => {
    const newProdutos = [...produtos];
    // Se for precoCompra, garantir que seja string
    const finalValue = field === 'precoCompra' ? String(value) : value;
    newProdutos[index] = { ...newProdutos[index], [field]: finalValue };
    
    // Calcular valor total se alterar quantidade ou valor unitário
    if (field === 'quantidade' || field === 'precoCompra') {
      const quantidade = Number(newProdutos[index].quantidade) || 0;
      const valorUnitario = Number(newProdutos[index].precoCompra) || 0;
      newProdutos[index].valorTotal = quantidade * valorUnitario;
    }
    
    setProdutos(newProdutos);
  };

  // Handler para input de valor unitário com vírgula
  const handlePrecoUnitarioChange = (index: number, value: string) => {
    const formattedValue = formatCurrencyInput(value);
    const numericValue = parseCurrencyToNumber(formattedValue);
    
    // Atualizar estado dos valores formatados
    setProdutoValues(prev => ({
      ...prev,
      [`precoUnitario_${index}`]: formattedValue
    }));
    
    // Atualizar o produto com o valor numérico
    updateProduto(index, 'precoCompra', numericValue);
  };

  // Selecionar produto
  const handleProdutoChange = (index: number, idProduto: number) => {
    const produto = produtosDisponiveis.find(p => p.id === idProduto);
    if (produto) {
      // Fazer todas as atualizações de uma vez
      const newProdutos = [...produtos];
      newProdutos[index] = {
        ...newProdutos[index],
        idProduto: idProduto,
        nomeProduto: produto.nomeProduto,
        referencia: produto.referencia
      };
      setProdutos(newProdutos);
    }
  };

  // Validar formulário
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.idFornecedor || formData.idFornecedor === '') {
      newErrors.idFornecedor = 'Fornecedor é obrigatório';
    }
    if (!formData.dataCompra) {
      newErrors.dataCompra = 'Data de compra é obrigatória';
    }
    if (!formData.dataEntradaEmEstoque) {
      newErrors.dataEntradaEmEstoque = 'Data de entrada em estoque é obrigatória';
    }
    if (produtos.length === 0) {
      newErrors.produtos = 'Adicione pelo menos um produto';
    } else if (produtos.some((produto) => produto.quantidade === '')) {
      newErrors.produtos = 'Informe a quantidade de todos os produtos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvar formulário
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Converter datas para o formato esperado pela API
    const convertToISODate = (dateString: string): string => {
      if (!dateString) return '';
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}T00:00:00`;
    };

    const dataCompra = convertToISODate(formData.dataCompra);
    const dataEntrada = convertToISODate(formData.dataEntradaEmEstoque);

    const requestData: NotaEntradaRequest = {
      idFornecedor: Number(formData.idFornecedor),
      dataCompra: dataCompra,
      dataEntrada: dataEntrada,
      valor: produtos.reduce((total, produto) => total + produto.valorTotal, 0),
      produtos: produtos.map(produto => ({
        ...produto,
        idNotaEntrada: 0,
        quantidade: Number(produto.quantidade),
        precoCompra: Number(produto.precoCompra).toFixed(2).replace('.', ','),
        CodigoDeBarras: produto.CodigoDeBarras || [],
        IdSacola: produto.IdSacola || [],
      }))
    };

    saveMutation.mutate(requestData);
  };

  const formatCurrency = (value: number) => {
    const safeValue = isNaN(value) ? 0 : (value || 0);
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(safeValue);
  };

  // Função para formatar valor monetário para input (permite digitar com vírgula)
  const formatCurrencyInput = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';

    const numericValue = Number(digits) / 100;
    return numericValue.toFixed(2).replace('.', ',');
  };

  // Função para converter string com vírgula para número
  const parseCurrencyToNumber = (value: string): number => {
    if (!value) return 0;
    // Substitui vírgula por ponto para conversão
    const numericValue = value.replace(',', '.');
    return parseFloat(numericValue) || 0;
  };

  // Função para formatar valor para exibição (com 2 casas decimais)
  const formatValueDisplay = (value: number): string => {
    const safeValue = isNaN(value) ? 0 : (value || 0);
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(safeValue);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            {isEditing ? 'Editar Nota de Entrada' : 'Nova Nota de Entrada'}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/entradas')}
          >
            Voltar
          </Button>
        </Box>

        {errors.produtos && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.produtos}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Fornecedor */}
            <Box>
              <Autocomplete
                options={fornecedores}
                getOptionLabel={(option) => option.nome}
                value={fornecedores.find((f) => f.id === formData.idFornecedor) || null}
                onChange={(_, newValue) => {
                  setFormData({ ...formData, idFornecedor: newValue ? newValue.id : '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Fornecedor"
                    error={!!errors.idFornecedor}
                    helperText={errors.idFornecedor}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />
            </Box>

            {/* Data de Compra */}
            <Box>
              <DatePickerField
                label="Data de Compra"
                value={formData.dataCompra}
                onChange={(value) => setFormData({ ...formData, dataCompra: value })}
                error={!!errors.dataCompra}
              />
            </Box>

            {/* Data de Entrada em Estoque */}
            <Box>
              <DatePickerField
                label="Data de Entrada em Estoque"
                value={formData.dataEntradaEmEstoque}
                onChange={(value) => setFormData({ ...formData, dataEntradaEmEstoque: value })}
                error={!!errors.dataEntradaEmEstoque}
              />
            </Box>
          </Box>

          {/* Produtos */}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Produtos
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openProdutosDialog}
              >
                Adicionar Produto
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor Unitário</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {produtos.map((produto, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <FormControl fullWidth size="small">
                          <Select
                            value={produto.idProduto || 0}
                            onChange={(e) => handleProdutoChange(index, Number(e.target.value))}
                            displayEmpty
                            renderValue={(selected) => {
                              if (selected === 0) return 'Selecione um produto';
                              const selectedProduct = produtosDisponiveis.find(p => p.id === selected);
                              return selectedProduct ? selectedProduct.nomeProduto : produto.nomeProduto || 'Selecione um produto';
                            }}
                          >
                            <MenuItem value={0}>Selecione um produto</MenuItem>
                            {produtosDisponiveis.map((prod) => (
                              <MenuItem key={prod.id} value={prod.id}>
                                {prod.nomeProduto}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>{produto.referencia}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          inputMode="numeric"
                          value={produto.quantidade}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*$/.test(value)) {
                              updateProduto(index, 'quantidade', value === '' ? '' : Number(value));
                            }
                          }}
                          sx={{ width: 100 }}
                          slotProps={{
                            htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="text"
                          value={produtoValues[`precoUnitario_${index}`] || formatValueDisplay(Number(produto.precoCompra) || 0)}
                          onFocus={(e) => {
                            if (!produtoValues[`precoUnitario_${index}`] && Number(produto.precoCompra) === 0) {
                              e.target.select();
                            }
                          }}
                          onChange={(e) => handlePrecoUnitarioChange(index, e.target.value)}
                          sx={{ width: 120 }}
                          slotProps={{
                            input: {
                              startAdornment: 'R$ '
                            }
                          }}
                          placeholder="0,00"
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(produto.valorTotal)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeProduto(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {produtos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum produto adicionado
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

      <Dialog open={produtosDialogOpen} onClose={closeProdutosDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Adicionar Produtos</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            fullWidth
            options={produtosDisponiveis}
            getOptionLabel={(option: any) =>
              option.informacoesExibir || `${option.referencia || ''} - ${option.nomeProduto || option.nome || 'Produto sem nome'}`.replace(/^\s*-\s*/, '')
            }
            value={produtosParaAdicionar}
            onChange={(_, value) => setProdutosParaAdicionar(value)}
            isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
            filterOptions={filterProdutos}
            disabled={produtosDisponiveis.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Selecione os produtos"
                placeholder="Pesquise ou selecione..."
              />
            )}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProdutosDialog} variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleAdicionarProdutos}
            variant="contained"
            disabled={produtosParaAdicionar.length === 0}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

          {/* Total e Ações */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Total: {formatCurrency(produtos.reduce((total, produto) => total + produto.valorTotal, 0))}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/entradas')}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default NotaEntradaForm;
